import React, { FC, Fragment, useContext, useMemo, useState } from 'react';
import {
    Box,
    Button,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { panNumberFeatureStatusTemplate } from 'apollo/templates/pan-number';
import { ListViewProfile, ListViewProfileInput, ListViewType, OrderListScope } from 'models/organization';
import { useListViewProfiles } from './use-list-view-profiles';
import ListViewProfileDialog from './list-view-profile-dialog.component';
import { ORDER_LIST_COLUMN_DEFINITIONS, OrderListColumnKey } from 'components/orders/lab-order-table/column-registry';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';

const ListViewsSettings: FC = () => {
    const { t: translate } = useTranslation(['lab-settings', 'common', 'orders', 'workflow', 'transport']);
    const { setAppToasterContent } = useContext(AppToasterContext);
    const { profiles, defaults, loading, saveProfile, deleteProfile, setDefaultProfile, saveLoading, deleteLoading } =
        useListViewProfiles();
    const { data: panNumberFeatureData } = useQuery(panNumberFeatureStatusTemplate);
    const panNumberEnabled: boolean = panNumberFeatureData?.currentLab?.panNumberEnabled ?? false;

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingProfile, setEditingProfile] = useState<ListViewProfile | undefined>();
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

    const labOrderProfiles = useMemo(
        () => profiles.filter((profile) => profile.listType === ListViewType.LAB_ORDER_LIST),
        [profiles],
    );

    const renderColumnsSummary = (profile: ListViewProfile) => {
        const visible = profile.columns.filter((column) => column.visible);
        if (!visible.length) return translate('lab-settings:LIST_VIEW_PROFILE_NO_COLUMNS');
        return visible
            .map((column) => {
                const definition = ORDER_LIST_COLUMN_DEFINITIONS[column.key as OrderListColumnKey];
                return definition ? translate(`${definition.i18nNamespace}:${definition.labelKey}`) : column.key;
            })
            .join(', ');
    };

    const openCreateDialog = () => {
        setEditingProfile(undefined);
        setDialogOpen(true);
    };

    const openEditDialog = (profile: ListViewProfile) => {
        setEditingProfile(profile);
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setEditingProfile(undefined);
    };

    const handleSave = async (input: ListViewProfileInput) => {
        const result = await saveProfile(input);
        if (result) {
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('lab-settings:LIST_VIEW_PROFILE_SAVED'),
            });
            handleDialogClose();
        }
    };

    const handleConfirmDelete = async () => {
        if (!confirmDeleteId) return;
        const success = await deleteProfile(confirmDeleteId);
        setConfirmDeleteId(null);
        if (success) {
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('lab-settings:LIST_VIEW_PROFILE_DELETED'),
            });
        }
    };

    const handleSetDefault = (scope: OrderListScope, profileId: string) => {
        const value = profileId === '' ? null : profileId;
        setDefaultProfile(scope, value);
    };

    const renderDefaultSelect = (scope: OrderListScope, label: string) => {
        const value = scope === 'MAIN' ? defaults.mainProfileId ?? '' : defaults.workflowProfileId ?? '';
        const labelId = `default-${scope.toLowerCase()}-profile-label`;
        return (
            <FormControl size="small" fullWidth>
                <InputLabel id={labelId}>{label}</InputLabel>
                <Select
                    labelId={labelId}
                    value={value}
                    label={label}
                    onChange={(e) => handleSetDefault(scope, e.target.value)}
                >
                    <MenuItem value="">
                        <em>{translate('lab-settings:LIST_VIEW_PROFILE_SYSTEM_DEFAULT')}</em>
                    </MenuItem>
                    {labOrderProfiles.map((profile) => (
                        <MenuItem key={profile._id} value={profile._id}>
                            {profile.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    };

    return (
        <Fragment>
            <Stack spacing={2}>
                <Box sx={{ maxWidth: '900px' }}>
                    <Typography variant="h6">{translate('lab-settings:LIST_VIEWS_LAB_ORDER_LIST')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {translate('lab-settings:LIST_VIEWS_DESCRIPTION')}
                    </Typography>
                </Box>

                <Box>
                    <Button startIcon={<AddIcon />} onClick={openCreateDialog}>
                        {translate('lab-settings:LIST_VIEW_PROFILE_ADD')}
                    </Button>
                </Box>

                <Paper variant="outlined">
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{translate('common:NAME')}</TableCell>
                                    <TableCell>{translate('lab-settings:LIST_VIEW_PROFILE_COLUMNS')}</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading && profiles.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={3}>
                                            <Skeleton variant="rectangular" height={32} />
                                        </TableCell>
                                    </TableRow>
                                ) : labOrderProfiles.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={3}>
                                            <Typography variant="body2" color="text.secondary">
                                                {translate('lab-settings:LIST_VIEW_PROFILE_NONE_YET')}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    labOrderProfiles.map((profile) => (
                                        <TableRow
                                            key={profile._id}
                                            hover
                                            onClick={() => openEditDialog(profile)}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <TableCell>
                                                <Typography variant="body2">{profile.name}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" color="text.secondary">
                                                    {renderColumnsSummary(profile)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                                <Tooltip title={translate('common:EDIT')}>
                                                    <IconButton onClick={() => openEditDialog(profile)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={translate('common:DELETE')}>
                                                    <IconButton
                                                        disabled={deleteLoading}
                                                        onClick={() => setConfirmDeleteId(profile._id)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Box sx={{ maxWidth: '900px' }}>
                    <Typography variant="subtitle2" mb={1}>
                        {translate('lab-settings:LIST_VIEW_PROFILE_LAB_DEFAULTS')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div" mb={1.5}>
                        {translate('lab-settings:LIST_VIEW_PROFILE_LAB_DEFAULTS_DESC')}
                    </Typography>
                    <Stack direction="row" spacing={2}>
                        {renderDefaultSelect('MAIN', translate('lab-settings:LIST_VIEW_PROFILE_DEFAULT_MAIN_LIST'))}
                        {renderDefaultSelect(
                            'WORKFLOW',
                            translate('lab-settings:LIST_VIEW_PROFILE_DEFAULT_WORKFLOW_LIST'),
                        )}
                    </Stack>
                </Box>
            </Stack>

            <ListViewProfileDialog
                open={dialogOpen}
                listType={ListViewType.LAB_ORDER_LIST}
                profile={editingProfile}
                saving={saveLoading}
                panNumberEnabled={panNumberEnabled}
                onClose={handleDialogClose}
                onSave={handleSave}
            />

            <ConfirmDialog
                open={!!confirmDeleteId}
                handleClose={() => setConfirmDeleteId(null)}
                handleConfirm={handleConfirmDelete}
                dialogTitle={translate('common:DELETE')}
                dialogContent={translate('lab-settings:LIST_VIEW_PROFILE_DELETE_CONFIRM')}
                confirmBtnLabel={translate('common:DELETE')}
            />
        </Fragment>
    );
};

export default ListViewsSettings;
